import L from 'leaflet';
import { forEach, get, isEmpty, isNil, sortBy } from 'lodash';
import turf from '@turf/turf';

import * as layerOps from '../../api/Layers/layer-operations';
import * as mapOps from '../../api/Maps/map-operations';
import * as missionOps from '../../api/Missions/mission-operations';
import * as videoOps from '../../api/Videos/video-operations';

const PANE = '10';
const DEFAULT_COLOR = '#3388ff';
const HIGHLIGHTED_COLOR = '#cc7700';
const POLYGON_STYLE = {
  fillOpacity: 0.2,
  opacity: 0.8,
  weight: 2,
  pane: PANE
};
const SELECTED_COLOR = '#55bb55';
const SELECTED_HIGHLIGHTED_COLOR = '#aa44aa';
const STYLE = {
  fillOpacity: 1,
  color: '#000000',
  weight: 1,
  pane: PANE
};
const LAYER_COLLECTION_NAME = 'layers';
const MAP_COLLECTION_NAME = 'maps';
const MISSION_COLLECTION_NAME = 'missions';
const VIDEO_COLLECTION_NAME = 'videos';

/**
 * Draws the list items in the map.
 *
 * @param {object} arg - The object argument.
 * @param {object} arg.leafletMap - The leaflet map object.
 * @param {object[]} arg.listItems - The list items.
 * @param {?object} arg.listItemsLayer - The GeoJSON layer where the list items will be painted.
 * @param {?object} arg.defaultLocation - The Feature Point to use if no location is encountered in item.
 * @param {?object} arg.itemCollectionName - The name of the displayed item collection.
 * @param {boolean} [arg.isUpdate=false] - Flag to determine if we are doing an update.
 * @param {?object} [arg.highlightedItemId=''] - The id of the item to be highlighted.
 * @param {?object} [arg.selectedItems={}] - The ids of the selected items.
 * @returns {object} The leaflet layer where the list items are painted.
 */
const drawListItems = ({
  leafletMap,
  listItems,
  listItemsLayer,
  defaultLocation,
  itemCollectionName,
  isUpdate = false,
  highlightedItemId = '',
  selectedItems = new Set()
}) => {
  let newListItemsLayerContainer = '';

  if (isNil(leafletMap.getPane(PANE))) leafletMap.createPane(PANE);

  if (!isUpdate) {
    newListItemsLayerContainer = L.geoJSON().addTo(leafletMap);
  } else {
    newListItemsLayerContainer = listItemsLayer;
    newListItemsLayerContainer.clearLayers();
  }

  if (!isEmpty(listItems)) {
    const sortedList = sortBy(listItems, listItem => {
      let area = 0;
      switch (itemCollectionName) {
        case LAYER_COLLECTION_NAME:
          if (!isEmpty(layerOps.getMapLocation(listItem)))
            area = -turf.area(turf.bboxPolygon(layerOps.getBBox(listItem)));
          break;
        case MAP_COLLECTION_NAME:
          if (!isEmpty(mapOps.getMapLocation(listItem)))
            area = -turf.area(turf.bboxPolygon(mapOps.getBBox(listItem)));
          break;
        default:
          break;
      }
      return area;
    });

    forEach(sortedList, listItem => {
      let fillColor = DEFAULT_COLOR;
      if (listItem._id === highlightedItemId) fillColor = HIGHLIGHTED_COLOR;
      if (selectedItems.has(listItem._id)) fillColor = SELECTED_COLOR;
      if (selectedItems.has(listItem._id) && listItem._id === highlightedItemId)
        fillColor = SELECTED_HIGHLIGHTED_COLOR;

      let itemLocation = defaultLocation;
      let bbox = {};
      switch (itemCollectionName) {
        case VIDEO_COLLECTION_NAME:
          itemLocation = videoOps.getLatLng0(listItem);
          break;
        case MISSION_COLLECTION_NAME:
          if (!isEmpty(missionOps.getTakeOffPoint(listItem)))
            itemLocation = turf.getCoord(missionOps.getTakeOffPoint(listItem));
          else itemLocation = turf.getCoord(defaultLocation);

          break;
        case LAYER_COLLECTION_NAME:
          if (!isEmpty(layerOps.getMapLocation(listItem))) {
            itemLocation = turf.getCoord(layerOps.getMapLocation(listItem));
            bbox = layerOps.getBBox(listItem);
          } else {
            itemLocation = turf.getCoord(defaultLocation);
          }
          break;
        case MAP_COLLECTION_NAME:
          if (!isEmpty(mapOps.getMapLocation(listItem))) {
            itemLocation = turf.getCoord(mapOps.getMapLocation(listItem));
            bbox = mapOps.getBBox(listItem);
          } else {
            itemLocation = turf.getCoord(defaultLocation);
          }
          break;
        default:
          break;
      }
      itemLocation = [itemLocation[1], itemLocation[0]];

      let myListItemsLayer;
      if (isEmpty(bbox)) myListItemsLayer = L.circleMarker(itemLocation, { ...STYLE, fillColor });
      else
        [myListItemsLayer] = L.geoJSON(turf.bboxPolygon(bbox), {
          ...POLYGON_STYLE,
          color: fillColor,
          fillColor
        }).getLayers();

      newListItemsLayerContainer.addLayer(myListItemsLayer.bindPopup(get(listItem, 'name', '')));
    });
  }
  return newListItemsLayerContainer;
};

export default drawListItems;
